import { Crumb, ItemType } from '@/ui/components/Breadcrumbs';
import { findProductByTag } from '@/utils/helpers';
import { Product } from '@/types/restResponses/info';
import { urlBuilder } from '@/utils/urlBuilder';

/**
 * Creates a product breadcrumb from a product slug
 */
export const createProductCrumb = (
  productSlug: string,
  products: Product[],
  locale: string
): Crumb | null => {
  const product = findProductByTag(products, productSlug);

  if (!product) {
    return null;
  }

  return {
    name: product.content || product.title,
    url: `${locale !== 'en' ? '/' + locale : ''}/product/${product.slug}`,
  };
};

/**
 * Modifies category breadcrumb items to use product topic URL pattern
 * when a product context is present
 */
export const modifyItemsForProductContext = (
  items: ItemType,
  productSlug: string,
  locale: string
): ItemType => {
  return items.map(item => {
    const isCategory = item?.type === 'category';

    if (isCategory) {
      const topicItem = {
        ...item,
        type: 'topic',
        parentSlug: productSlug,
      };
      
      return {
        name: item.title,
        url: urlBuilder({ item: topicItem, locale }),
      };
    }
    
    return item;
  });
};

/**
 * Builds complete breadcrumb items with product context
 */
export const buildBreadcrumbsWithProductContext = (
  items: ItemType,
  productSlug: string | null,
  products: Product[] | undefined,
  locale: string
): { productCrumb: Crumb | null; breadcrumbItems: ItemType } => {
  if (!productSlug || !products) {
    return { productCrumb: null, breadcrumbItems: items };
  }

  const productCrumb = createProductCrumb(productSlug, products, locale);

  if (!productCrumb) {
    return { productCrumb: null, breadcrumbItems: items };
  }

  const modifiedItems = modifyItemsForProductContext(items, productSlug, locale);
  const breadcrumbItems = [productCrumb, ...modifiedItems];

  return { productCrumb, breadcrumbItems };
};

